"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { LAUNCH_MODE } from "@/lib/limits";

const KEY = "tlr_launch_free_popup_v1";

/**
 * Popup de lanzamiento — visible para CADA visitante (una vez, luego se recuerda
 * en localStorage). Solo en LAUNCH_MODE (acceso anticipado): anuncia que todas
 * las funciones Plus están gratis para quien se registre, sin fecha límite fija.
 * Se auto-oculta cuando LAUNCH_MODE = false (al activar la facturación).
 */
export function LaunchFreePopup() {
  const [show, setShow] = useState(false);

  /**
   * ⚠️ No mostrar en los primeros segundos: un modal a pantalla completa sobre
   * el contenido al cargar es un «intrusive interstitial» — Google lo trata
   * como mala experiencia móvil y baja la página. Mostramos solo cuando el
   * usuario ya está leyendo: 40 % de scroll o 45 s de permanencia.
   */
  useEffect(() => {
    if (!LAUNCH_MODE) return;
    try {
      if (localStorage.getItem(KEY)) return;
    } catch {
      /* localStorage no disponible → mostrar igualmente */
    }

    let shown = false;
    const reveal = () => {
      if (shown) return;
      shown = true;
      setShow(true);
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop / Math.max(1, doc.scrollHeight - doc.clientHeight);
      if (scrolled >= 0.4) reveal();
    };

    // 45 s como red de seguridad para quien no hace scroll (móvil lento, etc.)
    const timer = setTimeout(reveal, 45_000);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  function close() {
    setShow(false);
    try {
      localStorage.setItem(KEY, "1");
    } catch {
      /* noop */
    }
  }

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="launch-popup-title"
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-emerald-500 p-[2px] shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-3xl bg-white p-6 sm:p-8 text-center">
          <button
            onClick={close}
            aria-label="Cerrar"
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full text-stone-400 hover:bg-stone-100 transition-colors text-lg"
          >
            ✕
          </button>

          <div className="text-5xl mb-3">🎉</div>
          <h2 id="launch-popup-title" className="text-xl sm:text-2xl font-black text-stone-900 mb-2 leading-tight">
            Plus <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">GRATIS</span> para todos
          </h2>
          <p className="text-sm text-stone-600 leading-relaxed mb-5">
            Estamos en <strong>acceso anticipado</strong>: <strong>todas las funciones Plus</strong> están
            abiertas <strong>gratis</strong> para cualquiera que se registre. Sin tarjeta, sin compromiso.
            No hay una fecha límite fija — la oferta puede terminar en cualquier momento, así que{" "}
            <strong>aprovéchala ahora</strong>.
          </p>

          <Link
            href="/auth/register?src=launch_popup"
            onClick={close}
            className="block w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold py-3.5 hover:opacity-95 transition-opacity shadow-lg shadow-violet-500/25"
          >
            Empezar gratis →
          </Link>
          <button onClick={close} className="mt-3 text-xs text-stone-400 hover:text-stone-600 transition-colors">
            Ahora no
          </button>
        </div>
      </div>
    </div>
  );
}
